import { useCallback } from "react";
import PageHeader from "@/components/PageHeader";
import IsometricGrid from "@/components/sketch/IsometricGrid";
import SketchToolbar from "@/components/sketch/SketchToolbar";
import CutList from "@/components/sketch/CutList";
import SketchManager from "@/components/sketch/SketchManager";
import { useSketchState, type GridPoint } from "@/hooks/use-sketch-state";

const PipeSketch = () => {
  const sketch = useSketchState();

  const constrainToAxis3 = useCallback((start: GridPoint, end: GridPoint): GridPoint => {
    const dx = end.gx - start.gx;
    const dy = end.gy - start.gy;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    const verticalAmount = Math.round((dx + dy) / 2);
    const vertDist = Math.abs(dx - verticalAmount) + Math.abs(dy - verticalAmount);

    if (ady <= adx && ady <= vertDist) {
      return { gx: end.gx, gy: start.gy };
    } else if (adx <= vertDist) {
      return { gx: start.gx, gy: end.gy };
    } else {
      return { gx: start.gx + verticalAmount, gy: start.gy + verticalAmount };
    }
  }, []);

  const handleGridClick = useCallback((point: GridPoint) => {
    if (sketch.mode === "draw") {
      if (!sketch.drawStart) {
        sketch.setDrawStart(point);
      } else {
        const end = constrainToAxis3(sketch.drawStart, point);
        sketch.addSegment(sketch.drawStart, end);
        sketch.setDrawStart(end);
      }
    } else if (sketch.mode === "fitting") {
      sketch.addFitting(point);
    } else if (sketch.mode === "delete") {
      sketch.deleteAtPoint(point);
    }
  }, [sketch, constrainToAxis3]);

  const hasContent = sketch.segments.length > 0 || sketch.fittings.length > 0;

  return (
    <div className="pb-20 md:pb-6">
      <PageHeader title="Pipe Sketch" subtitle="Isometric pipe sketch pad with auto cut list" />

      <div className="px-3 space-y-3">
        {/* Toolbar + Sketch Manager */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1">
            <SketchToolbar
              mode={sketch.mode}
              setMode={m => { sketch.setMode(m); sketch.setDrawStart(null); }}
              activeSize={sketch.activeSize}
              setActiveSize={sketch.setActiveSize}
              activeMaterial={sketch.activeMaterial}
              setActiveMaterial={sketch.setActiveMaterial}
              activeFitting={sketch.activeFitting}
              setActiveFitting={sketch.setActiveFitting}
              undo={sketch.undo}
              redo={sketch.redo}
              canUndo={sketch.canUndo}
              canRedo={sketch.canRedo}
              clearAll={sketch.clearAll}
            />
          </div>
          <div className="flex items-center">
            <SketchManager
              sketchList={sketch.sketchList}
              activeSketchId={sketch.activeSketchId}
              hasContent={hasContent}
              saveAsNewSketch={sketch.saveAsNewSketch}
              loadSketch={sketch.loadSketch}
              deleteSketch={sketch.deleteSketch}
              renameSketch={sketch.renameSketch}
              newBlankSketch={sketch.newBlankSketch}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-3">
          <div className="trade-card overflow-hidden" style={{ height: "min(70vh, 560px)" }}>
            <IsometricGrid
              segments={sketch.segments}
              fittings={sketch.fittings}
              mode={sketch.mode}
              drawStart={sketch.drawStart}
              onGridClick={handleGridClick}
              moveSegment={sketch.moveSegment}
              moveFitting={sketch.moveFitting}
            />
          </div>
          <div className="trade-card overflow-auto" style={{ maxHeight: "min(70vh, 560px)" }}>
            <CutList
              cutList={sketch.cutList}
              totalFootage={sketch.totalFootage}
              segmentCount={sketch.segments.length}
              fittingCount={sketch.fittings.length}
              segments={sketch.segments}
              fittings={sketch.fittings}
              sketchName={sketch.sketchList.find(s => s.id === sketch.activeSketchId)?.name}
            />
          </div>
        </div>

        <div className="text-xs text-muted-foreground text-center space-x-3">
          <span>🖱️ Click to draw — snaps to 3 axes (right, left, vertical)</span>
          <span>👆 Select mode: click & drag pipes or fittings to reposition</span>
          <span>⌨️ Ctrl+Z to undo</span>
          <span>🔍 Scroll to zoom</span>
          <span>✋ Pan mode to move the grid</span>
        </div>
      </div>
    </div>
  );
};

export default PipeSketch;
